import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, GraduationCap } from 'lucide-react';
import CourseCard from '../components/CourseCard';

export default function Library() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetch('/api/courses')
      .then((r) => r.json())
      .then((data) => setCourses(data.courses || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const paid = useMemo(() => courses.filter((c) => c.price_cents > 0), [courses]);
  const free = useMemo(() => courses.filter((c) => c.price_cents === 0), [courses]);

  const filtered = useMemo(() => {
    if (!query.trim()) return paid;
    const q = query.trim().toLowerCase();
    return paid.filter((c) =>
      [c.title, c.short_description, c.long_description]
        .filter(Boolean)
        .some((t) => t.toLowerCase().includes(q)),
    );
  }, [paid, query]);

  return (
    <>
      {/* ── Header ─────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-brand-light/60 via-white to-orange-50 pt-16 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          <span className="inline-block text-sm font-semibold text-brand bg-brand/10 px-4 py-1.5 rounded-full mb-5">
            Course Library
          </span>
          <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight text-wordmark leading-[1.1]">
            The full <span className="text-brand">course catalog</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
            Every course we offer — live workshops and self-paced training — in one place.
            Browse by topic, search by keyword, and learn on your schedule.
          </p>

          <div className="mt-8 max-w-md relative">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search courses…"
              className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
            />
          </div>
        </div>
      </section>

      {/* ── Catalog grid ───────────────────────────────────────── */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          {loading && <p className="text-slate-500">Loading…</p>}

          {!loading && paid.length === 0 && (
            <div className="text-center py-16 bg-gradient-to-br from-orange-50/60 to-white rounded-3xl border border-orange-100">
              <GraduationCap size={56} className="text-brand/30 mx-auto mb-4" />
              <p className="text-slate-700 text-lg font-bold">No courses yet</p>
              <p className="text-slate-500 mt-1">Check back soon — new releases coming.</p>
            </div>
          )}

          {!loading && paid.length > 0 && filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-700 text-lg font-bold">No matches for "{query}"</p>
              <button
                onClick={() => setQuery('')}
                className="mt-3 text-sm text-brand font-bold hover:underline"
              >
                Clear search
              </button>
            </div>
          )}

          {filtered.length > 0 && (
            <>
              <div className="flex items-baseline justify-between mb-6">
                <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500">
                  {filtered.length} course{filtered.length !== 1 ? 's' : ''}
                  {query && <span className="text-slate-400 normal-case font-medium tracking-normal"> matching "{query}"</span>}
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((c) => <CourseCard key={c.id} course={c} />)}
              </div>
            </>
          )}

          {free.length > 0 && (
            <div className="mt-12 text-center">
              <Link to="/tutorials" className="text-brand font-semibold hover:underline">
                Looking for free tutorials? Browse {free.length} →
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
